import { subDays } from "date-fns";
import type { OutlookHistoryDeal } from "./target-outlook";

export const OUTLOOK_HISTORY_POLICY = { version: "bounded-history-v1", maximumShare: .05, minimumWins: 5 } as const;
export type OutlookHistoryQualityOptions = {
  pipelineById: ReadonlyMap<string, string>;
  recoveredById?: ReadonlyMap<string, { value?: number; closedAt?: Date }>;
  allowRecentCreationFallback?: boolean;
};
const validDate = (date: Date | null): date is Date => Boolean(date && Number.isFinite(date.getTime()));
const validAmount = (value: number) => Number.isFinite(value) && value >= 0;
const dayMs = 86_400_000;

/** Opt-in, company/scoped history only. Never writes estimated dates or amounts to source records. */
export function prepareOutlookHistoryQuality(source: readonly OutlookHistoryDeal[], asOf: Date, maximumDays: number,
  options: OutlookHistoryQualityOptions) {
  const from = subDays(asOf, 365 + Math.ceil(Math.min(366, Math.max(0, maximumDays))));
  const relevant = (deal: OutlookHistoryDeal) => validDate(deal.createdAt) && deal.createdAt >= from && deal.createdAt < asOf;
  let recoveredAmounts = 0;
  let recoveredDates = 0;
  const deals = source.map(deal => {
    const recovery = options.recoveredById?.get(String(deal.id));
    let { value, closedAt } = deal;
    if (!validAmount(value) && recovery?.value !== undefined && validAmount(recovery.value)) {
      value = recovery.value;
      if (relevant(deal)) recoveredAmounts++;
    }
    if (deal.isWon && (!validDate(closedAt) || !validDate(deal.createdAt) || closedAt < deal.createdAt || closedAt >= asOf)
      && validDate(deal.createdAt) && validDate(recovery?.closedAt ?? null)
      && recovery!.closedAt! >= deal.createdAt && recovery!.closedAt! < asOf) {
      closedAt = recovery!.closedAt!;
      if (relevant(deal)) recoveredDates++;
    }
    return { ...deal, value, closedAt };
  });
  const usableWin = (deal: OutlookHistoryDeal) => relevant(deal) && deal.isWon && validDate(deal.closedAt)
    && deal.closedAt >= deal.createdAt! && deal.closedAt < asOf;
  const donors = deals.filter(deal => usableWin(deal) && validAmount(deal.value));
  const estimatedIds = new Set<string>();
  const estimatedAmounts = new Set<string>();
  // A distribution of observed durations, never a fabricated per-deal close date.
  const winAges = new Map<string, Array<{ age: number; share: number }>>();
  const unresolvedIds = new Set<string>();
  for (const deal of deals) {
    if (!relevant(deal) || !deal.isWon) continue;
    const id = String(deal.id);
    const needsAmount = !validAmount(deal.value);
    // A future-dated outcome is not observed yet. Keep it censored, never
    // manufacture an earlier win to make the forecast calculable.
    const needsTiming = !validDate(deal.closedAt) || deal.closedAt < deal.createdAt!;
    if (!needsAmount && !needsTiming) continue;
    estimatedIds.add(id);
    const pipeline = options.pipelineById.get(id);
    const peers = !pipeline ? [] : donors.filter(peer => options.pipelineById.get(String(peer.id)) === pipeline);
    if (needsAmount) {
      if (peers.length < OUTLOOK_HISTORY_POLICY.minimumWins) unresolvedIds.add(id);
      else {
        deal.value = peers.reduce((sum, peer) => sum + peer.value, 0) / peers.length;
        estimatedAmounts.add(id);
      }
    }
    if (needsTiming) {
      const age = (asOf.getTime() - deal.createdAt!.getTime()) / dayMs;
      const observed = peers.map(peer => (peer.closedAt!.getTime() - peer.createdAt!.getTime()) / dayMs)
        .filter(duration => duration >= 0 && duration < age);
      if (observed.length < OUTLOOK_HISTORY_POLICY.minimumWins) unresolvedIds.add(id);
      else winAges.set(id, observed.map(duration => ({ age: duration, share: 1 / observed.length })));
    }
  }
  return { deals, estimatedIds, estimatedAmounts, winAges, unresolvedIds,
    summary: { method: OUTLOOK_HISTORY_POLICY.version, relevantDeals: deals.filter(relevant).length,
      recoveredAmounts, recoveredDates, estimatedAmounts: estimatedAmounts.size, estimatedDates: winAges.size,
      unresolvedDeals: unresolvedIds.size } };
}
